// /virtual-keyboard — the page. The board is server-rendered (first screen
// without waiting for this module); here it learns to type.
//
// Two ways in, one result (typer.js):
//   · the board — mouse or finger; Shift and AltGr are one-shot, Caps stays;
//   · the physical keyboard — remapped by POSITION (KeyboardEvent.code), so a
//     person with an English keyboard types Tatar or Russian letters where the
//     national layout has them. Shortcuts (Ctrl/⌘ + key), IME composition and
//     keys outside the 48 character positions keep their native meaning.
// The text is never stored: a virtual keyboard is where people type passwords.

import { Typer, levelOf } from './typer.js';

const $ = (id) => document.getElementById(id);
const DATA = JSON.parse(($('vkData') || {}).textContent || '{}');
const POS = DATA.positions || [];
const POS_INDEX = Object.fromEntries(POS.map((c, i) => [c, i]));
const L = DATA.layouts || {};
const SLUG = 'virtual-keyboard';
const ta = $('vkText');
const board = $('vkBoard');
const sel = $('vkLayout');
if (!ta || !board || !sel || !POS.length) throw new Error('virtual-keyboard: markup or data missing');

const ae = (ev, data) => { try { if (window.__ae) window.__ae(ev, data || {}); } catch (_) {} };
const saidOnce = {};
const once = (f, extra) => { if (saidOnce[f]) return; saidOnce[f] = 1; ae('feature_use', Object.assign({ slug: SLUG, f }, extra || {})); };
const settings = window.__toolSettings;

let layoutId = sel.value in L ? sel.value : (DATA.def in L ? DATA.def : Object.keys(L)[0]);
const typer = new Typer(L[layoutId]);
const st = { shift: false, caps: false, altgr: false, physShift: false, physAltgr: false };

function hasAltGr(l) { return (l.a || []).some(Boolean) || (l.as || []).some(Boolean); }
function hasIso(l) { const i = POS_INDEX.IntlBackslash; return i != null && !!l.n[i] && l.n[i] !== l.n[POS_INDEX.Backslash]; }

// ── board legends ─────────────────────────────
function paint() {
    const l = L[layoutId];
    const shift = st.shift || st.physShift, altgr = st.altgr || st.physAltgr;
    board.classList.toggle('vk-iso', hasIso(l));
    board.classList.toggle('vk-has-altgr', hasAltGr(l));
    board.querySelectorAll('[data-pos]').forEach((k) => {
        const pos = +k.dataset.pos;
        const cur = levelOf(l, pos, { shift, caps: st.caps, altgr });
        const main = cur.ch;
        const alt = altgr ? '' : levelOf(l, pos, { shift, caps: st.caps, altgr: true }).ch;
        const m = k.querySelector('.vk-m'), a = k.querySelector('.vk-a');
        if (m) m.textContent = main;
        if (a) a.textContent = alt && alt !== main ? alt : '';
        k.classList.toggle('vk-dead', cur.dead);
        k.classList.toggle('vk-pending', !!typer.pendingDead && main === typer.pendingDead);
        k.disabled = !main;
        k.setAttribute('aria-label', main === ' ' ? 'space' : main);
    });
    const intl = board.querySelector('[data-pos="' + POS_INDEX.IntlBackslash + '"]');
    if (intl) intl.hidden = !hasIso(l);
    board.querySelectorAll('[data-act="altgr"]').forEach((b) => { b.hidden = !hasAltGr(l); });
    pressed('shift', st.shift); pressed('caps', st.caps); pressed('altgr', st.altgr);
    const sp = board.querySelector('[data-act="space"] .vk-m');
    if (sp) sp.textContent = l.name;
    ta.dir = l.rtl ? 'rtl' : 'ltr';
    ta.lang = l.lang || '';
}
function pressed(act, on) {
    board.querySelectorAll('[data-act="' + act + '"]').forEach((b) => { b.setAttribute('aria-pressed', on ? 'true' : 'false'); b.classList.toggle('vk-on', on); });
}

// ── text edits ────────────────────────────────
function model() { return { value: ta.value, start: ta.selectionStart, end: ta.selectionEnd }; }
function commit(m) {
    if (m.value !== ta.value) ta.value = m.value;
    try { ta.setSelectionRange(m.start, m.end); } catch (_) {}
    ta.dispatchEvent(new Event('input', { bubbles: true }));
    paint();
}
function typeChar(ch, via, dead) {
    if (!ch) return;
    once('type', { via, layout: layoutId });
    commit(typer.input(model(), ch, !!dead));
}
function backspace() { commit(typer.backspace(model())); }
function moveCaret(d) {
    typer.reset();
    const p = Math.max(0, Math.min(ta.value.length, (d < 0 ? ta.selectionStart : ta.selectionEnd) + d));
    try { ta.setSelectionRange(p, p); } catch (_) {}
    paint();
}
function keepFocus() {
    // on a touch device the textarea is inputmode=none while the board types: the
    // phone's own keyboard does not slide over the board
    if (document.activeElement !== ta) { try { ta.focus({ preventScroll: true }); } catch (_) {} }
}

// ── the board ─────────────────────────────────
function onBoardKey(k) {
    keepFocus();
    if (k.dataset.pos != null) {
        const l = L[layoutId];
        const r = levelOf(l, +k.dataset.pos, { shift: st.shift, caps: st.caps, altgr: st.altgr });
        typeChar(r.ch, 'board', r.dead);
        if (st.shift || st.altgr) { st.shift = false; st.altgr = false; paint(); }
        return;
    }
    switch (k.dataset.act) {
        case 'shift': st.shift = !st.shift; paint(); break;
        case 'caps': st.caps = !st.caps; paint(); break;
        case 'altgr': st.altgr = !st.altgr; paint(); break;
        case 'space': typeChar(' ', 'board'); break;
        case 'enter': typer.reset(); commit((({ value, start, end }) => ({ value: value.slice(0, start) + '\n' + value.slice(end), start: start + 1, end: start + 1 }))(model())); break;
        case 'tab': typer.reset(); commit((({ value, start, end }) => ({ value: value.slice(0, start) + '\t' + value.slice(end), start: start + 1, end: start + 1 }))(model())); break;
        default: break;
    }
}
// a press keeps the caret where it was: the board never takes the focus
board.addEventListener('mousedown', (e) => { if (e.target.closest('button')) e.preventDefault(); });
board.addEventListener('click', (e) => {
    const k = e.target.closest('button');
    if (!k || k.disabled || !board.contains(k)) return;
    if (k.dataset.act === 'back' || k.dataset.act === 'left' || k.dataset.act === 'right') {
        // a click that is not a held press (keyboard, assistive tech) still does one step
        if (e.detail === 0) { keepFocus(); stepOf(k)(); }
        return;
    }
    onBoardKey(k);
});
function stepOf(k) {
    if (k.dataset.act === 'back') return backspace;
    if (k.dataset.act === 'left') return () => moveCaret(-1);
    return () => moveCaret(1);
}
// held Backspace and arrows repeat — the site primitive owns timing and the click of the press
board.querySelectorAll('[data-act="back"],[data-act="left"],[data-act="right"]').forEach((k) => {
    const step = stepOf(k);
    if (typeof window.__holdRepeat === 'function') window.__holdRepeat(k, () => { keepFocus(); step(); return true; });
    else k.addEventListener('pointerdown', () => { keepFocus(); step(); });
});

// ── the physical keyboard, remapped by position ──
const remap = $('vkRemap');
function remapOn() { return !remap || remap.getAttribute('aria-checked') !== 'false'; }
function isAltGr(e) { try { if (e.getModifierState && e.getModifierState('AltGraph')) return true; } catch (_) {} return e.ctrlKey && e.altKey; }
function flash(pos, on) { const k = board.querySelector('[data-pos="' + pos + '"]'); if (k) k.classList.toggle('vk-down', on); }
ta.addEventListener('keydown', (e) => {
    if (e.key === 'Shift' && !st.physShift) { st.physShift = true; paint(); }
    if ((e.key === 'AltGraph' || (e.ctrlKey && e.altKey)) && !st.physAltgr) { st.physAltgr = true; paint(); }
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === 'Backspace' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); backspace(); return; }
    if (/^Arrow|^Home$|^End$|^Page/.test(e.key)) { typer.reset(); return; }
    const pos = POS_INDEX[e.code];
    if (pos == null || !remapOn()) return;
    const altgr = isAltGr(e);
    if ((e.ctrlKey || e.altKey) && !altgr) return;
    if (e.metaKey) return;
    const caps = e.getModifierState ? e.getModifierState('CapsLock') : false;
    const r = levelOf(L[layoutId], pos, { shift: e.shiftKey, caps, altgr });
    e.preventDefault();
    flash(pos, true);
    if (r.ch) { once('physical', { layout: layoutId }); typeChar(r.ch, 'keys', r.dead); }
});
ta.addEventListener('keyup', (e) => {
    const pos = POS_INDEX[e.code];
    if (pos != null) flash(pos, false);
    if (e.key === 'Shift' || (!e.shiftKey && st.physShift)) { st.physShift = false; paint(); }
    if (e.key === 'AltGraph' || e.key === 'Control' || e.key === 'Alt' || (st.physAltgr && !isAltGr(e))) { st.physAltgr = false; paint(); }
});
ta.addEventListener('blur', () => { if (st.physShift || st.physAltgr) { st.physShift = st.physAltgr = false; paint(); } board.querySelectorAll('.vk-down').forEach((k) => k.classList.remove('vk-down')); });
// the caret moved by mouse or touch: the accent or the Korean block stays where it was
ta.addEventListener('pointerdown', () => { typer.reset(); });
if (remap) remap.addEventListener('click', () => {
    const on = !remapOn();
    remap.setAttribute('aria-checked', on ? 'true' : 'false');
    remap.classList.toggle('on', on);
    try { settings && settings.save(SLUG, 'remap', on); } catch (_) {}
    ae('feature_use', { slug: SLUG, f: 'remap', on: on ? 1 : 0 });
    keepFocus();
});

// ── layout ────────────────────────────────────
function setLayout(id, how) {
    if (!(id in L)) return;
    layoutId = id;
    typer.setLayout(L[id]);
    st.shift = st.altgr = false;
    if (sel.value !== id) sel.value = id;
    paint();
    try { settings && settings.save(SLUG, 'layout', id); } catch (_) {}
    if (how) ae('feature_use', { slug: SLUG, f: 'layout', layout: id, how });
}
sel.addEventListener('change', () => { setLayout(sel.value, 'select'); keepFocus(); });

// ── actions ───────────────────────────────────
const T = DATA.t || {};
async function copyText() {
    const v = ta.value;
    if (!v) { if (window.__toast) window.__toast(T.empty || '', 'info'); return; }
    let ok = false;
    try { await navigator.clipboard.writeText(v); ok = true; } catch (_) {
        try { ta.select(); ok = document.execCommand('copy'); } catch (__) {}
    }
    if (window.__toast) window.__toast(ok ? (T.copied || '') : (T.copy_failed || ''), ok ? 'success' : 'error');
    if (ok) {
        once('copy', { layout: layoutId });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ layout: layoutId, chars: v.length, via: 'copy' }); } catch (_) {}
    }
}
const copyBtn = $('vkCopy'), clearBtn = $('vkClear'), searchBtn = $('vkSearch'), nativeBtn = $('vkNative');
if (copyBtn) copyBtn.addEventListener('click', copyText);
if (clearBtn) clearBtn.addEventListener('click', () => { typer.reset(); ta.value = ''; ta.dispatchEvent(new Event('input', { bubbles: true })); keepFocus(); paint(); });
if (searchBtn) searchBtn.addEventListener('click', () => {
    const v = ta.value.trim();
    if (!v) { keepFocus(); return; }
    once('search', { layout: layoutId });
    window.open((DATA.search || 'https://www.google.com/search?q=') + encodeURIComponent(v), '_blank', 'noopener');
});
function syncCount() { const c = $('vkCount'); if (c) c.textContent = String([...ta.value].length); }
ta.addEventListener('input', syncCount);

// ── the phone's own keyboard ──────────────────
const TOUCH = (navigator.maxTouchPoints || 0) > 0 && !(window.matchMedia && window.matchMedia('(any-pointer:fine)').matches);
function setNative(on) {
    ta.setAttribute('inputmode', on ? 'text' : 'none');
    if (nativeBtn) { nativeBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); nativeBtn.classList.toggle('vk-on', on); }
}
if (TOUCH) {
    setNative(false);
    if (nativeBtn) {
        nativeBtn.hidden = false;
        nativeBtn.addEventListener('click', () => {
            const on = ta.getAttribute('inputmode') === 'none';
            setNative(on);
            ae('feature_use', { slug: SLUG, f: 'native_keyboard', on: on ? 1 : 0 });
            try { ta.blur(); ta.focus({ preventScroll: true }); } catch (_) {}
        });
    }
}

// ── start ─────────────────────────────────────
try {
    const saved = settings && settings.load(SLUG, 'layout', null);
    if (saved && saved in L && saved !== layoutId) setLayout(saved, null);
    const r = settings && settings.load(SLUG, 'remap', true);
    if (remap && r === false) { remap.setAttribute('aria-checked', 'false'); remap.classList.remove('on'); }
} catch (_) {}
paint();
syncCount();
board.classList.add('vk-live');
// «the tool is up» for the layout's module door (data-expect), and a handle for stands
window.__virtualKeyboard = { setLayout: (id) => setLayout(id, null), layout: () => layoutId };
